// Parses accumulated SSE (text/event-stream) text into structured events.
// Pure and stream-agnostic on purpose: HelpChatPanel feeds it whatever text
// has accumulated so far and keeps `remainder` around to prepend to the next
// chunk, so a chunk boundary landing mid-event never loses data.
//
// One event per `\n\n`-terminated block; within a block, an `event:` line
// sets the event name (default "message") and a `data:` line carries a JSON
// payload. A block with no `data:` line (e.g. a bare SSE comment/heartbeat
// like ": ping") is skipped, not returned as an event.
//
// The SSE spec permits CRLF line endings, and a proxy in front of the API
// could introduce them even if the server itself never does - normalizing
// before splitting means a CRLF stream parses identically to LF instead of
// never matching "\n\n" at all (every frame piling up in `remainder`
// forever, with no event - and no error - ever surfacing, a bug caught in
// review). Safe to do on every call: `buffer` is always the full
// accumulated text (including a normalized remainder from the previous
// call), so there's no chunk-boundary case where a bare "\r" from one call
// and a bare "\n" from the next need joining before normalizing.
export function parseSseBuffer(buffer) {
  const events = [];
  const normalized = buffer.replace(/\r\n/g, "\n");
  const parts = normalized.split("\n\n");
  const remainder = parts.pop() || "";

  for (const block of parts) {
    let eventName = "message";
    // Collected as an array (one entry per `data:` line, a single leading
    // space stripped rather than the whole line trimmed) so a value with
    // significant trailing whitespace right at a line split isn't silently
    // eaten. The SSE spec joins consecutive `data:` lines with "\n", but
    // every payload here is a single JSON value, and JSON forbids a raw,
    // unescaped newline inside a string - literally following the spec
    // would turn a split that lands mid-string into invalid JSON (silently
    // dropped below) instead of the corrupted-but-parseable text this used
    // to produce. Joining with "" instead reconstructs the original value
    // exactly for a JSON payload split at any point, and is a no-op for the
    // structural-boundary splits (After a "," or ":") the spec's multi-line
    // convention is actually for, since JSON is whitespace-insensitive
    // between tokens there (a bug caught in review).
    const dataLines = [];
    for (const line of block.split("\n")) {
      if (line.startsWith("event:")) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        const rest = line.slice(5);
        dataLines.push(rest.startsWith(" ") ? rest.slice(1) : rest);
      }
    }
    if (dataLines.length === 0) continue;
    const dataStr = dataLines.join("");
    if (!dataStr) continue;

    let data;
    try {
      data = JSON.parse(dataStr);
    } catch (_e) {
      continue;
    }
    // A valid JSON payload that isn't an object (null, a bare number,
    // "true", ...) has no fields for a consumer to read - every event
    // handler downstream dereferences `data.<field>` unconditionally, so
    // `data: null` on a known event name would otherwise throw a TypeError
    // that aborts the whole read loop (a bug caught in review).
    if (!data || typeof data !== "object") continue;
    events.push({ event: eventName, data });
  }

  return { events, remainder };
}
